import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { submissionService } from '@/services/submission.service'
import type { SubmissionFilters, CreateSubmissionInput, UpdateSubmissionInput } from '@/types'

export const SUBMISSIONS_QUERY_KEY = ['submissions']

export function useSubmissions(filters: SubmissionFilters = {}) {
  return useQuery({
    queryKey: [...SUBMISSIONS_QUERY_KEY, 'list', filters],
    queryFn: () => submissionService.listSubmissions(filters)
  })
}

export function useSubmission(id: string | undefined) {
  return useQuery({
    queryKey: [...SUBMISSIONS_QUERY_KEY, 'detail', id],
    queryFn: () => submissionService.getSubmission(id!),
    enabled: !!id
  })
}

export function useSubmissionCounts() {
  return useQuery({
    queryKey: [...SUBMISSIONS_QUERY_KEY, 'counts'],
    queryFn: () => submissionService.getSubmissionCounts()
  })
}

export function useCreateSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateSubmissionInput) =>
      submissionService.createSubmission(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_QUERY_KEY })
    }
  })
}

export function useUpdateSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateSubmissionInput }) =>
      submissionService.updateSubmission(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_QUERY_KEY })
      queryClient.setQueryData([...SUBMISSIONS_QUERY_KEY, 'detail', data.id], data)
    }
  })
}

export function useDeleteSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => submissionService.deleteSubmission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_QUERY_KEY })
    }
  })
}

export function useArchiveSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => submissionService.archiveSubmission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBMISSIONS_QUERY_KEY })
    }
  })
}
